import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HiX } from 'react-icons/hi';

const ApplicationFormModal = ({ showForm, setShowForm, formData, handleChange, resetForm }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  return (
    <AnimatePresence>
      {showForm && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowForm(false)}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[95vh] flex flex-col">

              {/* Header */}
              <div className="bg-gradient-to-r from-primary-600 to-primary-700 text-white p-6 rounded-t-2xl flex items-center justify-between">
                <h2 className="text-2xl font-bold">Application Form</h2>
                <HiX
                  onClick={() => setShowForm(false)}
                  className="w-6 h-6 cursor-pointer hover:text-gray-200"
                />
              </div>

              {/* Form */}
              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  setIsSubmitting(true);

                  const form = new FormData();
                  form.append("name", formData.name);
                  form.append("email", formData.email);
                  form.append("phone", formData.phone);
                  form.append("education", formData.education);
                  form.append("workExperience", formData.workExperience);
                  form.append("nightShift", formData.nightShift);
                  form.append("resume", formData.resume);

                  try {
                    const response = await fetch(
                      `${process.env.REACT_APP_API_BASE_URL}/api/careers/apply`,
                      {
                        method: "POST",
                        body: form,
                      }
                    );

                    const result = await response.json();

                    if (response.ok) {
                      alert("✅ Application submitted successfully!");
                      resetForm();
                      setShowForm(false);
                    } else {
                      alert("⚠️ " + (result.error || "Something went wrong."));
                    }
                  } catch (err) {
                    console.error(err);
                    alert("❌ Network error. Check your connection.");
                  } finally {
                    setIsSubmitting(false);
                  }
                }}
                className="p-6 space-y-6 overflow-y-auto"
              >
                {/* Name */}
                <div>
                  <label className="block font-semibold mb-2">Full Name *</label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full p-3 border rounded-xl"
                    placeholder="Enter your full name"
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block font-semibold mb-2">Email *</label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full p-3 border rounded-xl"
                    placeholder="yourname@email.com"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label className="block font-semibold mb-2">Phone Number *</label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full p-3 border rounded-xl"
                    placeholder="+91 9876543210"
                  />
                </div>

                {/* Education */}
                <div>
                  <label className="block font-semibold mb-2">Education *</label>
                  <input
                    type="text"
                    name="education"
                    required
                    value={formData.education}
                    onChange={handleChange}
                    className="w-full p-3 border rounded-xl"
                    placeholder="B.Tech / MBA / Degree ..."
                  />
                </div>

                {/* Work Experience */}
                <div>
                  <label className="block font-semibold mb-2">Work Experience *</label>
                  <textarea
                    name="workExperience"
                    required
                    value={formData.workExperience}
                    onChange={handleChange}
                    rows={4}
                    className="w-full p-3 border rounded-xl resize-none"
                    placeholder="Describe your experience"
                  ></textarea>
                </div>

                {/* Resume */}
                <div>
                  <label className="block font-semibold mb-2">Resume (PDF/DOC) *</label>
                  <input
                    type="file"
                    name="resume"
                    required
                    accept=".pdf,.doc,.docx"
                    onChange={handleChange}
                    className="w-full p-3 border rounded-xl"
                  />
                </div>

                {/* Night Shift */}
                <div>
                  <label className="block font-semibold mb-2">
                    Are you comfortable with Night Shift? *
                  </label>
                  <div className="flex gap-6">
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="nightShift"
                        value="yes"
                        required
                        checked={formData.nightShift === "yes"}
                        onChange={handleChange}
                      />
                      Yes
                    </label>

                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="nightShift"
                        value="no"
                        required
                        checked={formData.nightShift === "no"}
                        onChange={handleChange}
                      />
                      No
                    </label>
                  </div>
                </div>

                {/* Submit Buttons */}
                <div className="flex gap-4 pt-4">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`flex-1 bg-primary-600 text-white py-4 rounded-xl font-bold flex items-center justify-center gap-2 ${
                      isSubmitting ? 'opacity-70 cursor-not-allowed' : 'hover:bg-primary-700'
                    }`}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      'Submit'
                    )}
                  </button>

                  <button
                    type="button"
                    disabled={isSubmitting}
                    onClick={() => setShowForm(false)}
                    className={`px-8 py-4 border-2 border-gray-300 rounded-xl ${
                      isSubmitting ? 'opacity-70 cursor-not-allowed' : ''
                    }`}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ApplicationFormModal;
